import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Column,
  Editing,
  Lookup,
  Texts,
  Toolbar,
  Item as ToolbarItem,
} from 'devextreme-react/data-grid';
import DropDownButton from 'devextreme-react/drop-down-button';
import { SquireDataGrid } from '../squire-data-grid';
import { SquirePopup } from '../squire-popup/squire-popup';
import { CompanyContactEditor } from '~/components/contact-editor/company-contact-editor';
import { ContactEditorApiService, ContactSelectorApiService } from '~/services';

const emptyStyle = { fontSize: '12px', color: '#888', margin: '8px 0 0 0' };
const titleStyle = { fontSize: '13px', fontWeight: 600, color: '#555' };

// The two ways a contact joins a client. Kept out of the grid's edit row: a picker with expanding
// name fields inside a DevExtreme edit cell was cramped, and the SelectBox misbehaved after a
// create because the grid re-rendered the cell underneath it (WK-565).
const CONTACT_ACTION_EXISTING = 'existing';
const CONTACT_ACTION_NEW = 'new';
const ADD_CONTACT_ACTIONS = [
  { key: CONTACT_ACTION_EXISTING, text: 'Add existing contact' },
  { key: CONTACT_ACTION_NEW, text: 'Add new contact' },
];

/**
 * Grid of the contacts chosen for a single project client. Mirrors the client grids: the grid's own
 * "+" adds a row whose Name cell is a ContactPickerCell — pick one of the company's contacts, or
 * create a new one inline; the trash icon removes one. Landlord clients additionally get an editable
 * Type column. The contact can only be chosen while adding — on an existing row its identity is
 * fixed (change = remove + re-add), which matches the backend (a ProjectContact update only edits
 * the landlord type).
 *
 * Persistence-agnostic. Batch consumers (transition form) pass onChange and receive the full
 * selection. Immediate consumers (edit form) pass onAdd / onRemove / onTypeChange to drive
 * per-mutation API calls; in that mode the grid cancels its own edit and the parent persists,
 * refetches, and remounts this editor.
 *
 * @param {number} companyId - the company whose contacts are selectable
 * @param {Array<{contactId:number, fullName?:string, preferredPhone?:string, preferredEmail?:string,
 *   landlordContactTypeId?:number, projectContactId?:number}>} [initialContacts=[]] - seed selection
 * @param {Function} [onChange] - (contacts[]) => void; batch mode (full selection after any change)
 * @param {Function} [onAdd] - (addedContact) => void; immediate mode
 * @param {Function} [onRemove] - (removedContact) => void; immediate mode
 * @param {Function} [onTypeChange] - (contact, landlordContactTypeId) => void; immediate mode
 * @param {Object} [landlordContactTypeDataSource] - when provided, render an editable Type column
 */
export function ClientContactsEditor({
  companyId,
  initialContacts = [],
  onChange,
  onAdd,
  onRemove,
  onTypeChange,
  landlordContactTypeDataSource,
}) {
  const immediate = !!(onAdd || onRemove || onTypeChange);
  const isLandlord = !!landlordContactTypeDataSource;

  const apiService = useMemo(() => new ContactSelectorApiService(), []);
  const contactEditorApiService = useMemo(() => new ContactEditorApiService(), []);
  const gridRef = useRef(null);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [selected, setSelected] = useState(initialContacts);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [contactCreateState, setContactCreateState] = useState({ canConfirm: false, confirm: () => {} });

  // Load the company's contacts once — they back the inline Lookup and supply phone/email.
  // (The resolve runs after the await, so it isn't a synchronous setState within the effect.)
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    // The sorted variant returns a DataSource whose load() applies the ordering, so the inline
    // Lookup lists the company's contacts alphabetically instead of in database order (WK-565).
    const ds = apiService.getContactsByCompanyDataSourceSorted(companyId);
    ds.load().then((items) => {
      if (!cancelled) setCompanyContacts(items ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [apiService, companyId]);

  const contactsById = useMemo(
    () => new Map(companyContacts.map((c) => [c.contactId, c])),
    [companyContacts]
  );

  // Display rows: fill phone/email from the loaded company contacts where a seeded row lacks them
  // (the edit-form read path doesn't carry them). Derived, so it never emits onChange.
  const rows = useMemo(
    () =>
      selected.map((c) => {
        const source = contactsById.get(c.contactId);
        if (!source) return c;
        return {
          ...c,
          preferredPhone: c.preferredPhone ?? source.preferredPhone,
          preferredEmail: c.preferredEmail ?? source.preferredEmail,
        };
      }),
    [selected, contactsById]
  );

  const emit = useCallback(
    (next) => {
      setSelected(next);
      if (onChange) onChange(next);
    },
    [onChange]
  );

  const handleRowInserting = useCallback(
    (e) => {
      e.cancel = true; // immediate: parent persists + remounts; batch: we add to local state below.
      const contactId = e.data?.contactId;
      if (contactId == null) return;
      if (selected.some((c) => c.contactId === contactId)) return; // already chosen
      const source = contactsById.get(contactId);
      const added = {
        contactId,
        fullName: source?.fullName,
        preferredPhone: source?.preferredPhone,
        preferredEmail: source?.preferredEmail,
        landlordContactTypeId: e.data.landlordContactTypeId ?? null,
      };
      if (immediate) {
        if (onAdd) onAdd(added);
      } else {
        emit([...selected, added]);
      }
    },
    [selected, contactsById, immediate, onAdd, emit]
  );

  const handleRowRemoving = useCallback(
    (e) => {
      e.cancel = true;
      const contact = e.data;
      if (immediate) {
        if (onRemove) onRemove(contact);
      } else {
        emit(selected.filter((c) => c.contactId !== contact.contactId));
      }
    },
    [selected, immediate, onRemove, emit]
  );

  // Only the landlord Type is editable on an existing row (the contact editor is disabled below).
  const handleRowUpdating = useCallback(
    (e) => {
      e.cancel = true;
      const newType =
        'landlordContactTypeId' in e.newData
          ? e.newData.landlordContactTypeId
          : e.oldData.landlordContactTypeId;
      const contact = { ...e.oldData, landlordContactTypeId: newType };
      if (immediate) {
        if (onTypeChange) onTypeChange(contact, newType);
      } else {
        emit(
          selected.map((c) =>
            c.contactId === contact.contactId ? { ...c, landlordContactTypeId: newType } : c
          )
        );
      }
    },
    [selected, immediate, onTypeChange, emit]
  );

  const handleEditorPreparing = useCallback((e) => {
    // The contact can only be chosen while adding; on an existing row its identity is fixed.
    if (e.parentType === 'dataRow' && e.dataField === 'contactId' && !e.row?.isNewRow) {
      e.editorOptions.disabled = true;
    }
  }, []);

  const handleAddContactClick = useCallback((e) => {
    if (e.itemData.key === CONTACT_ACTION_NEW) {
      setShowCreateForm(true);
      return;
    }
    gridRef.current?.instance().addRow();
  }, []);

  // The estimate persists immediately, so the whole graph the form collected — name, job title,
  // role, phones, emails — goes up in one CreateContactCommand, which creates the Contact and its
  // CompanyContact together. The company edit form stages the same payload into its own DTO instead.
  // Throws on failure (fetchApiOrThrow), keeping the popup open and attaching nothing.
  const handleCreateNewContact = useCallback(async (newCC) => {
    const created = await contactEditorApiService.createContact({
      firstName: newCC.firstName,
      lastName: newCC.lastName,
      companyContacts: {
        toCreate: [{
          companyId,
          jobTitle: newCC.jobTitle,
          location: newCC.location,
          roleId: newCC.roleId,
          statusId: newCC.statusId,
          phoneNumbers: newCC.phoneNumbers,
          emailAddresses: newCC.emailAddresses,
        }],
        toUpdate: {},
        toDelete: [],
      },
    });

    const phones = newCC.phoneNumbers?.toCreate ?? [];
    const emails = newCC.emailAddresses?.toCreate ?? [];
    const added = {
      contactId: created.id,
      fullName: `${created.firstName} ${created.lastName}`,
      // Taken from what was just entered so the Phone/Email cells aren't blank until a refetch.
      preferredPhone: (phones.find((p) => p.preferred) ?? phones[0])?.number,
      preferredEmail: (emails.find((e) => e.preferred) ?? emails[0])?.address,
      landlordContactTypeId: null,
    };

    // Fold it into the lookup list so the Name cell can resolve it without waiting for a refetch.
    setCompanyContacts((prev) =>
      prev.some((c) => c.contactId === added.contactId) ? prev : [...prev, added]
    );

    if (immediate) {
      if (onAdd) onAdd(added);
    } else {
      emit([...selected, added]);
    }
    setShowCreateForm(false);
  }, [contactEditorApiService, companyId, immediate, onAdd, emit, selected]);

  // Derive phone/email onto the editing row the moment a contact is picked, so they show before save
  // (and refresh if the selection is changed again). Without this, the read-only phone/email cells
  // stay blank until the row is saved and this editor remounts from the refetched data.
  const setContactCellValue = useCallback((newData, value) => {
    newData.contactId = value;
    const source = contactsById.get(value);
    newData.preferredPhone = source?.preferredPhone ?? '';
    newData.preferredEmail = source?.preferredEmail ?? '';
  }, [contactsById]);

  if (!companyId) {
    return <p style={emptyStyle}>Select a company first to add contacts.</p>;
  }

  return (
    <>
      <SquireDataGrid
        ref={gridRef}
        dataSource={rows}
        keyExpr="contactId"
        showBorders={true}
        columnAutoWidth={true}
        noDataText="No contacts added yet."
        onRowInserting={handleRowInserting}
        onRowRemoving={handleRowRemoving}
        onRowUpdating={isLandlord ? handleRowUpdating : undefined}
        onEditorPreparing={handleEditorPreparing}
      >
        <Editing
          mode="row"
          useIcons={true}
          allowAdding={true}
          allowUpdating={isLandlord}
          allowDeleting={true}
        >
          <Texts confirmDeleteMessage="Remove this contact from the client?" />
        </Editing>

        <Toolbar>
          <ToolbarItem location="before" render={() => <span style={titleStyle}>Contacts</span>} />
          {/* Replaces the grid's own addRowButton: "Add existing contact" opens the inline row it
              used to open, "Add new contact" opens the popup below. */}
          <ToolbarItem location="after" render={() => (
            <DropDownButton
              elementAttr={{ 'data-testid': 'add-client-contact-button' }}
              text="+ Add Contact"
              stylingMode="outlined"
              items={ADD_CONTACT_ACTIONS}
              displayExpr="text"
              keyExpr="key"
              useSelectMode={false}
              onItemClick={handleAddContactClick}
              dropDownOptions={{ width: 200 }}
            />
          )} />
        </Toolbar>

        <Column dataField="contactId" caption="Name" setCellValue={setContactCellValue}>
          <Lookup dataSource={companyContacts} displayExpr="fullName" valueExpr="contactId" />
        </Column>
        <Column dataField="preferredPhone" caption="Phone" allowEditing={false} />
        <Column dataField="preferredEmail" caption="Email" allowEditing={false} />
        {isLandlord && (
          <Column dataField="landlordContactTypeId" caption="Type">
            <Lookup dataSource={landlordContactTypeDataSource} displayExpr="value" valueExpr="id" />
          </Column>
        )}
      </SquireDataGrid>

      {showCreateForm && (
        <SquirePopup
          visible
          nested
          title="Add new contact"
          onClose={() => setShowCreateForm(false)}
          onSave={() => contactCreateState.confirm()}
          saveButtonText="Add"
          saveButtonDisabled={!contactCreateState.canConfirm}
        >
          {/* The same form the company edit form opens, so a contact is captured identically on
              both surfaces. It reports the entered values; this component persists them. */}
          <CompanyContactEditor
            companyContact={null}
            isCompanySide={true}
            createMode="new"
            companyId={companyId}
            onCreate={handleCreateNewContact}
            onCancelCreate={() => setShowCreateForm(false)}
            hideInternalActions
            onCreateStateChange={setContactCreateState}
          />
        </SquirePopup>
      )}
    </>
  );
}
